/**
 * PRENUMERATIONSBANNERN — det enda stället i appen som säger till INNAN det är
 * för sent.
 *
 * TRE BESKED, OCH BARA ETT I TAGET:
 *   pausad      arLast (subscription_status 'locked'). Skrivningen är stängd,
 *               läsningen och exporten är det inte (beslut 11). Knappen är
 *               vägen ut, och den vägen finns bara på Prenumeration.tsx.
 *   obetald     subscription_status 'past_due'. Stripe fick inte betalt men
 *               har inte gett upp; firman fungerar än.
 *   provperiod  subscription_status 'trialing'. DATUMET är hela beskedet, och
 *               det finns bara i SUBSCRIPTION (current_period_end) — inte i ME
 *               och inte i licensbeviset, som bara bär statusen.
 * Ordningen är den ovan: en pausad firma ska inte få läsa om sin provperiod.
 *
 * BARA CHEFEN SER DEN. En montör kan inte göra något åt en obetald faktura, och
 * ett besked han inte kan agera på är en oro utan utgång. Bokföraren ser den
 * inte heller: han står i FULL_VIEW_ROLES men rutterna som betalar kräver
 * ÄGAREN (beslut 14), och skärmen han når hittar han i inställningarna.
 *
 * ETT ANROP, OCH BARA I PROVPERIODEN. Sessionen bär redan statusen ur ME, så
 * 'locked' och 'past_due' kostar ingen hämtning. Bara 'trialing' behöver
 * datumet, och då hämtas SUBSCRIPTION en gång. En firma som betalar lägger
 * alltså inte ett extra anrop på varje besök på startsidan.
 *
 * INGEN NY DELAD KOMPONENT. Bannern är byggd av Yta, Bricka och Knapp ur ui/ —
 * ui/index.ts rörs inte. Behövs formen på fler ställen är det ett eget beslut
 * att flytta dit den, inte en bieffekt av den här skärmen.
 *
 * VARNINGEN TRAPPAS UPP SISTA DYGNET. En banner som ser likadan ut fem dagar
 * före som samma kväll firman pausas har slutat vara en varning; se `bradskande`
 * längst ner.
 */

import type { ReactNode } from 'react';
import type { Millis } from '@delat/types';
import { Bricka, Installningsrad, Knapp, Yta } from '../ui';
import { dagarMellan, idag, tillArbetsdag, visaDag, visaKortDatum } from '../app/datum';
import { useInloggad } from '../app/session';
import { useNavigering } from '../app/navigering';
import { useProvperiodensSlut } from './prenumeration-data';

/** "Torsdag 27 augusti" -> "torsdag 27 augusti", för mitt i en mening. */
const gemenDag = (text: string): string => `${text.charAt(0).toLowerCase()}${text.slice(1)}`;

/** Hela dagar kvar av provperioden. Negativt = ankaret har redan passerat. */
const dagarKvar = (slut: Millis): number => dagarMellan(idag(), tillArbetsdag(slut));

/** "i dag", "i morgon", "om 5 dagar, torsdag 27 augusti". */
const narSlutar = (slut: Millis): string => {
  const dag = tillArbetsdag(slut);
  const kvar = dagarKvar(slut);
  if (kvar === 0) return 'i dag';
  if (kvar === 1) return 'i morgon';
  return `om ${kvar} dagar, ${gemenDag(visaDag(dag))}`;
};

const Banner = ({
  rubrik,
  bricka,
  knapp,
  onKnapp,
  bradskande,
  children,
}: {
  readonly rubrik: string;
  readonly bricka?: string;
  readonly knapp: string;
  readonly onKnapp: () => void;
  /** Mörk fullbred knapp i stället för tyst. För de lägen som kostar pengar. */
  readonly bradskande: boolean;
  readonly children: ReactNode;
}) => (
  <Yta
    rubrik={
      <>
        {rubrik}
        {bricka !== undefined && (
          <>
            {' '}
            <Bricka ton="pagaende">{bricka}</Bricka>
          </>
        )}
      </>
    }
  >
    <p style={{ margin: 0 }}>{children}</p>
    <div style={{ marginTop: 'var(--gap-kort)' }}>
      <Knapp variant={bradskande ? 'stor' : 'stor-tyst'} onClick={onKnapp}>
        {knapp}
      </Knapp>
    </div>
  </Yta>
);

export const Prenumerationsbanner = () => {
  const { firmaId, aktiv, arChef, arLast } = useInloggad();
  const { ga } = useNavigering();

  const provperiod = aktiv.subscription_status === 'trialing';
  const slut = useProvperiodensSlut(firmaId, arChef && provperiod);

  // Hooken måste ropas i varje rendering — därför står den ovanför, och först
  // här faller allt bort för den som inte är chef.
  if (!arChef) return null;

  const till = (): void => ga('prenumeration');

  if (arLast) {
    return (
      <Banner
        rubrik="Firman är pausad"
        bricka="pausad"
        knapp="Ordna betalningen"
        onKnapp={till}
        bradskande
      >
        Allt går att läsa och exportera som vanligt. Det som är stängt är att spara
        nytt — timmarna, milen och jobben ni redan lagt in ligger kvar.
      </Banner>
    );
  }

  if (aktiv.subscription_status === 'past_due') {
    return (
      <Banner
        rubrik="Betalningen gick inte igenom"
        bricka="obetald"
        knapp="Se prenumerationen"
        onKnapp={till}
        bradskande
      >
        Kortet nekades eller har gått ut. Firman fungerar som vanligt så länge, men
        ordnas det inte pausas den — och då går inget nytt att spara.
      </Banner>
    );
  }

  // Provperioden. Utan datum ur SUBSCRIPTION säger vi ingenting alls: "tar slut,
  // vet inte när" är sämre än tystnad.
  if (provperiod && slut !== null) {
    const kvar = dagarKvar(slut);
    /*
      VARNINGEN TRAPPAS UPP. Bannern var tyst hela vägen — också den dag texten
      sa "Den tar slut i dag", timmar innan nattsvepet (cron 21:00 UTC) pausar
      firman. Sista dygnet får därför den mörka knappen.

      OCH ANKARET KAN REDAN HA PASSERAT: mellan att dagen gått och att svepet
      kört står status kvar på 'trialing'. Då är "tar slut i dag" en lögn om ett
      datum som redan är förbi, och firman ska i stället få veta vad som händer
      i natt.
     */
    if (kvar < 0) {
      return (
        <Banner
          rubrik="Provperioden är slut"
          knapp="Teckna Klartid"
          onKnapp={till}
          bradskande
        >
          Firman pausas i natt om ingen tecknar. Allt ni lagt in ligger kvar och går
          att läsa och exportera — det som stängs är att spara nytt.
        </Banner>
      );
    }
    if (kvar <= 1) {
      return (
        <Banner rubrik="Provperioden" knapp="Teckna Klartid" onKnapp={till} bradskande>
          Den tar slut {narSlutar(slut)}. Tecknar du innan dess rullar allt vidare utan
          avbrott, och samma dag blir firmans betalningsdag varje månad.
        </Banner>
      );
    }
    /*
      DEN LUGNA PROVPERIODEN ÄR EN RAD, INTE ETT KORT (2026-09-11). Startsidan
      ska rymma de fyra kaklen och kalendern utan rullning på en iPhone 14,
      och det gjorde den inte de sju dagar kortet stod här: 205 px för ett
      besked som ryms på en rad. Hela texten — betalningsdagen och att allt
      rullar vidare — står på Prenumeration.tsx, dit raden leder.
    */
    return (
      <Installningsrad
        etikett="Provperioden slutar"
        varde={visaKortDatum(slut)}
        atgard={
          <Knapp variant="piller-mini" onClick={till}>
            Teckna
          </Knapp>
        }
      />
    );
  }

  return null;
};
